import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { MobileFormFooter } from '@/components/primitives/MobileFormFooter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { createFirefighter } from '@/api/firefighters';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  FirefighterSchema,
  type FirefighterValues,
} from './FirefighterSchema';

export default function FirefighterCreate() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { can } = useAuth();
  // Mirrors the backend gate (utils/firefighter_privacy.py): only the tiers
  // that can delete a firefighter see these fields.
  const canSeePersonal = can('firefighter', 'delete');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FirefighterValues>({
    resolver: zodResolver(FirefighterSchema),
    defaultValues: { name: '', surname: '', note: '', contact: '', address: '', emergency_contact: '', medical_aid_details: '' },
    mode: 'onTouched',
  });

  const onSubmit = async (values: FirefighterValues) => {
    setError(null);
    setSubmitting(true);
    try {
      await createFirefighter({
        name: values.name,
        surname: values.surname,
        note: values.note || null,
        contact: values.contact || null,
        address: values.address || null,
        emergency_contact: values.emergency_contact || null,
        medical_aid_details: values.medical_aid_details || null,
        // Scope the new firefighter to the selected FPA. The backend also
        // enforces this (enforce_tenant_ownership), but sending it keeps the
        // super-admin create path working under the active FPA scope.
        tenant_id: tenantId ?? undefined,
      });
      toast.success(`${values.name} ${values.surname} added`);
      navigate('/firefighters');
    } catch (err) {
      console.error('Create firefighter failed:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.response?.data?.detail || e?.message || 'Failed to create firefighter');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Add firefighter"
        breadcrumbs={[
          { label: 'Firefighters', to: '/firefighters' },
          { label: 'New' },
        ]}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pb-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Section title="Identity">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="given-name" disabled={submitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Surname <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="family-name" disabled={submitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Section>

          {canSeePersonal && (
          <Section
            title="Personal details"
            description="Reference only, not used anywhere else in the app yet. Visible to FPA administrators."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact number</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="tel" autoComplete="tel" disabled={submitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emergency_contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency contact</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Name and number" disabled={submitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="medical_aid_details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Medical aid details</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} placeholder="Scheme, plan and member number" disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>
          )}
          <Section title="Notes" description="Optional internal notes.">
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={4}
                      placeholder="Any details about training, role, or contact."
                      disabled={submitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          {/* Desktop inline actions */}
          <div className="hidden justify-end gap-2 md:flex">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/firefighters')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save firefighter'}
            </Button>
          </div>

          <MobileFormFooter
            isSubmitting={submitting}
            cancelTo="/firefighters"
            saveLabel="Save firefighter"
          />
        </form>
      </Form>
    </PageShell>
  );
}
